import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Consumable } from "./ConsumableOverview/ConsumableOverview";

export function useGetConsumables({
  pagination,
  filter,
  search,
  sort,
  enabled,
  showUnrelated = false,
  year,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
  showUnrelated?: boolean;
  year?: number;
}) {
  return useQuery({
    queryKey: ["consumables", "list", pagination, filter, search, sort, year, showUnrelated],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<Consumable>>("/consumables", {
          params: {
            ...(year ? { year } : {}),
            show_unrelated: showUnrelated,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export function useGetConsumable({
  consumableId,
  enabled,
}: {
  consumableId: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["consumables", "detail", consumableId, "details"],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios.get<Consumable>("/consumables/" + consumableId).then((result) => result.data),
  });
}

export interface ConsumableUnit {
  start_unit: string;
  target_unit: string;
  factor: number | null;
  status: "complete" | "incomplete" | "validating";
}

export type GetConsumableUnits = ConsumableUnit[];

export function useGetConsumableUnits({ consumableId }: { consumableId: number }) {
  return useQuery({
    queryKey: ["consumables", "detail", consumableId, "units"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetConsumableUnits>("/consumables/" + consumableId + "/units")
        .then((result) => result.data),
  });
}

export interface UpdateConsumableUnit {
  start_unit: string;
  target_unit: string;
  factor: number | null;
}

export function useUpdateConsumableUnit() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({
      consumableId,
      consumableUnit,
    }: {
      consumableId: number;
      consumableUnit: UpdateConsumableUnit;
    }) =>
      axios.post<{ unit_status: "complete" | "incomplete"; consumable: Consumable }>(
        "/consumables/" + consumableId + "/units",
        consumableUnit
      ),
    onSuccess: (
      { data: { unit_status: newStatus, consumable } },
      { consumableId, consumableUnit }
    ) => {
      smartQueryClient.invalidateQueries(["dashboard"]);
      smartQueryClient.invalidateQueries(["consumables", "list"]);
      smartQueryClient.invalidateQueries(["consumables", "detail", consumableId, "details"]);

      smartQueryClient.setQueryData<GetConsumableUnits>(
        ["consumables", "detail", consumableId, "units"],
        (consumableUnits) => {
          return consumableUnits
            ? consumableUnits.map((unit) => {
                if (
                  unit.start_unit === consumableUnit.start_unit &&
                  unit.target_unit === consumableUnit.target_unit
                ) {
                  return {
                    ...unit,
                    status: newStatus,
                  };
                } else {
                  return unit;
                }
              })
            : undefined;
        }
      );

      smartQueryClient.setQueryData(["consumables", "detail", consumableId, "details"], consumable);
    },
  });
}

export interface ConsumablePatch {
  name?: string;
  direct_impact_dataset_id?: number;
  eol_impact_dataset_id?: number;
  clear?: ("direct_impact_dataset_id" | "eol_impact_dataset_id")[];
}

export function usePatchConsumable() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ consumableId, patch }: { consumableId: number; patch: ConsumablePatch }) =>
      axios.patch<Consumable>("/consumables/" + consumableId, patch),
    onSuccess: ({ data: consumable }, { consumableId }) => {
      smartQueryClient.invalidateQueries(["consumables", "list"]);
      smartQueryClient.invalidateQueries(["dashboard"]);
      smartQueryClient.invalidateQueries(["consumables", "detail", consumableId, "units"]);
      smartQueryClient.setQueryData(["consumables", "detail", consumableId, "details"], consumable);
    },
    onMutate: ({ consumableId, patch }) => {
      smartQueryClient.setQueryData<Consumable>(
        ["consumables", "detail", consumableId, "details"],
        (data) => {
          if (!data) return undefined;
          return { ...data, ...patch };
        }
      );
    },
  });
}

export function useAddConsumable() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name?: string }) => axios.post<Consumable>("/consumables", { name }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["consumables", "list"]);
    },
  });
}

export function useDeleteConsumable() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ consumableId }: { consumableId: number }) =>
      axios.delete("/consumables/" + consumableId),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["consumables"]);
    },
  });
}

export interface ConsumableRelation {
  type: "product_proxy" | "product_source" | "product" | "facility";
  row_id: string;
  id: number;
  name: string;
}

export function useGetConsumableRelations({
  consumableId,
  pagination,
  enabled,
}: {
  consumableId: number;
  pagination?: Pagination;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["consumables", "detail", consumableId, "relations", pagination],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<ConsumableRelation>>("/consumables/" + consumableId + "/relations", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
          },
        })
        .then((result) => result.data),
  });
}
export interface ConsumableRelationPerYear {
  year: number | null;
  facility_count: number;
  product_count: number;
}

export function useGetConsumableRelationsPerYear({
  consumableId,
  enabled,
}: {
  consumableId: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["consumables", "detail", consumableId, "relations", "per_year"],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<ConsumableRelationPerYear[]>("/consumables/" + consumableId + "/relations_per_year")
        .then((result) => result.data),
  });
}

export function useConsumableStatusPerYear() {
  return useQuery({
    queryKey: ["consumables", "list", "years"],
    keepPreviousData: true,
    queryFn: () => axios.get<YearStatus[]>("/consumables/years", {}).then((result) => result.data),
  });
}
